import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/student/PageHeader";
import { MultipleChoices } from "@/pages/Student/Exercises/ExerciseTypes/MultipleChoices";
import { Colors, useLayoutCommon } from "@/contexts/LayoutContext";
import { delay } from "@/lib/utils";
import { LevelTestQuestion } from "@/services/student/level-test.interfaces";
import { ExerciseItem } from "@/services/student/exercise.interfaces";
import { useLevelTest } from "@/hooks/student/useLevelTest";
import { useLevelTestProgress } from "@/contexts/LevelTestProgressContext";
import TestProgressPanel from "@/components/student/LevelTest/TestProgressPanel";
import config from "@/config/config";

const LevelingTestContent: React.FC = () => {
  const { setBackgroundColor } = useLayoutCommon();
  const navigate = useNavigate();
  const { 
    questionsHistory,
    currentQuestion,
    isTestCompleted,
    runLevelTest,
    submitAnswer,
    answers,
    sessionId
  } = useLevelTest();
  const { state: progressState, refresh: refreshProgress } = useLevelTestProgress();
  const questionsContainerRef = useRef<HTMLDivElement>(null);
  const hasRunRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const [sessionStartAt, setSessionStartAt] = useState<number | null>(null);
  const [elapsedLabel, setElapsedLabel] = useState<string>("0:00");

  // Utilitário simples para formatar mm:ss
  const formatElapsed = (ms: number) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };
  
  // Log state changes
  useEffect(() => {
    console.log("🔍 [COMPONENT] LevelingTest - State changed:", {
      questionsHistoryCount: questionsHistory.length,
      currentQuestion: currentQuestion ? {
        id: currentQuestion.id,
        questionText: currentQuestion.questionText,
        optionsCount: currentQuestion.options?.length || 0
      } : null,
      isTestCompleted,
      answersCount: answers.length,
      questionsHistoryDetails: questionsHistory.map((q, index) => ({
        index,
        id: q.id,
        questionText: q.questionText,
        optionsCount: q.options?.length || 0
      }))
    });
  }, [questionsHistory, currentQuestion, isTestCompleted, answers]);

  useEffect(() => {
    setBackgroundColor(Colors.WHITE);
  }, [setBackgroundColor]);

  // Redirecionar para a página de resultado quando o teste for concluído
  useEffect(() => {
    if (isTestCompleted) {
      navigate("/student/level-test/result", { replace: true });
    }
  }, [isTestCompleted, navigate]);

  // Executar teste se não há questão atual e não foi executado ainda
  useEffect(() => {
    console.log("🔍 [COMPONENT] LevelingTest - useEffect runLevelTest trigger check:", {
      currentQuestion: currentQuestion?.id,
      hasRunRef: hasRunRef.current,
      isTestCompleted,
      shouldRun: !currentQuestion && !hasRunRef.current && !isTestCompleted
    });
    
    if (!currentQuestion && !hasRunRef.current && !isTestCompleted) {
      console.log("🔍 [COMPONENT] LevelingTest - Starting runLevelTest");
      hasRunRef.current = true;
      // Fallback: iniciar contador imediatamente ao iniciar o teste
      if (!sessionStartAt) {
        const start = Date.now();
        setSessionStartAt(start);
        setElapsedLabel(formatElapsed(0));
        timerRef.current = window.setInterval(() => {
          setElapsedLabel(formatElapsed(Date.now() - start));
        }, 1000);
      }
      runLevelTest();
    }
  }, [currentQuestion, runLevelTest, isTestCompleted]);

  // Iniciar/atualizar timer quando a sessão iniciar e parar quando finalizar
  useEffect(() => {
    // Iniciar quando obtivermos um sessionId e ainda não temos start
    if (sessionId && !sessionStartAt) {
      const start = Date.now();
      setSessionStartAt(start);
    }
  }, [sessionId, sessionStartAt]);

  // Efeito separado para gerenciar o intervalo do timer
  useEffect(() => {
    // Se não tivermos start time ou se o teste acabou, não rodamos o timer
    if (!sessionStartAt || isTestCompleted) {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Se já tiver um timer rodando, não criamos outro
    if (timerRef.current) return;

    // Atualizar imediatamente
    setElapsedLabel(formatElapsed(Date.now() - sessionStartAt));
    
    // Iniciar intervalo
    timerRef.current = window.setInterval(() => {
      setElapsedLabel(formatElapsed(Date.now() - sessionStartAt));
    }, 1000);

    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [sessionStartAt, isTestCompleted]);

  // Atualizar painel de progresso quando o sessionId estiver disponível
  useEffect(() => {
    if (sessionId) {
      console.log("🔍 [COMPONENT] LevelingTest - Refreshing progress debug for session:", sessionId);
      refreshProgress(sessionId);
    }
  }, [sessionId, refreshProgress]);

  // Effect para scroll automático quando questão atual muda
  useEffect(() => {
    if (currentQuestion && questionsHistory.length > 0) {
      // Aguardar render completo antes de fazer scroll
      delay(300).then(() => {
        scrollToNewQuestion();
      });
    }
  }, [currentQuestion, questionsHistory.length]);

  // Função para fazer scroll suave para a nova questão
  const scrollToNewQuestion = () => {
    if (questionsContainerRef.current) {
      const container = questionsContainerRef.current;
      const lastQuestion = container.lastElementChild as HTMLElement;
      
      if (lastQuestion) {
        lastQuestion.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }
  };

  // Função auxiliar para preparar dados de resposta anterior
  const getPreviousAnswerData = (question: LevelTestQuestion, answer: any) => {
    // Mapear IDs para textos (agora funciona porque IDs estão corretos após conversão Order->ID)
    const selectedTexts = answer.selectedOptionIds
      .map((id: string) => {
        const option = question.options.find((opt: any) => opt.id === id);
        return option?.text;
      })
      .filter((text: string | undefined) => text !== undefined);
    
    return {
      selectedOptions: selectedTexts,
      isCorrect: answer.isCorrect,
      correctOptions: answer.isCorrect ? undefined : [] // Não temos mais isCorrect nas opções
    };
  };

  // Função para processar resposta do teste de nivelamento
  const handleLevelTestAnswer = async (selectedOptions: string[], item: ExerciseItem) => {
    try {
      // Usar hook para submeter resposta
      const result = await submitAnswer(item.id, selectedOptions);
      
        return {
          isCorrect: result.isCorrect,
          feedback: result.feedback,
          expectedAnswer: result.expectedAnswer?.filter(Boolean) as string[] || []
        };
    } catch (error) {
      console.error("Error submitting answer:", error);
      return {
        isCorrect: false,
        feedback: "Erro ao enviar resposta. Tente novamente.",
        expectedAnswer: []
      };
    }
  };

  const renderQuestion = (item: LevelTestQuestion, key: React.Key, index: number, answer?: any) => {
    console.log("🔍 [COMPONENT] renderQuestion - Starting render:", {
      questionId: item.id,
      questionText: item.questionText,
      questionType: item.questionType,
      difficultyLevel: item.difficultyLevel,
      skillArea: item.skillArea,
      hasAnswer: !!answer,
      optionsCount: item.options?.length || 0,
      optionsDetails: item.options?.map(opt => ({
        id: opt.id,
        text: opt.text,
        order: opt.order
      })) || [],
      index,
      key
    });
    
    // Converter LevelTestQuestion para ExerciseItem que MultipleChoices espera
    const exerciseItem: ExerciseItem = {
      id: item.id,
      title: `Questão ${index + 1}`, // Número da questão
      type: "multiple_choice",
      content: {
        question: item.questionText,
        multipleCorrectAnswers: false, // Level test sempre tem resposta única
        options: item.options.map(opt => ({
          id: opt.id,
          text: opt.text,
          order: opt.order || 1 // Default order
        }))
      },
      moduleId: "level-test",
      createdAt: new Date(),
    };
    
    console.log("🔍 [COMPONENT] renderQuestion - ExerciseItem created:", {
      exerciseId: exerciseItem.id,
      exerciseTitle: exerciseItem.title,
      contentQuestion: exerciseItem.content?.question,
      contentOptionsCount: exerciseItem.content?.options?.length || 0,
      contentOptions: exerciseItem.content?.options || []
    });

    // Adicionar animação de entrada para questões novas (não é a primeira)
    const isNewQuestion = index > 0;
    const animationClass = isNewQuestion 
      ? "animate-in slide-in-from-bottom-4 fade-in duration-500" 
      : "";

    return (
      <div key={key} className={`${animationClass}`}>
        <MultipleChoices 
          item={exerciseItem} 
          moduleId="level-test"
          onAnswer={handleLevelTestAnswer}
          onVerifyClick={(selectedOptions) => {
            console.log("Questão respondida:", selectedOptions);
          }}
          readonly={!!answer}
          previousAnswer={answer ? getPreviousAnswerData(item, answer) : undefined}
        />
      </div>
    );
  };

  const questionKey = (item: LevelTestQuestion) => item.id;

  const isLoading = !currentQuestion && !isTestCompleted && questionsHistory.length === 0;

  // Dados para o painel de progresso
  const progressData = {
    currentQuestionNumber: questionsHistory.length + (currentQuestion ? 1 : 0),
    totalQuestions: 20, // TODO: Obter total real quando disponível
    correctAnswers: answers.filter((answer: any) => answer.isCorrect).length,
    timeElapsed: elapsedLabel,
    difficultyLevel: currentQuestion?.difficultyLevel ? currentQuestion.difficultyLevel.toString() : "Iniciante",
    // Dados de debug do contexto
    phase: progressState.debug?.phase,
    phaseGroup: progressState.debug?.phaseGroup,
    currentCycleIndex: progressState.debug?.currentCycleIndex,
    cycleCorrect: progressState.debug?.cycleCorrect,
    cycleTotal: progressState.debug?.cycleTotal,
    pendingInCycle: progressState.debug?.pendingInCycle,
    poolCandidates: progressState.debug?.poolCandidates,
    decisionHint: progressState.debug?.decisionHint,
    minLevel: progressState.debug?.minLevel,
    maxLevel: progressState.debug?.maxLevel,
    levelRangeSize: progressState.debug?.levelRangeSize,
  };

  return (
    <div className="max-w-[1800px] w-full">
      {/* Progress Panel - Fixed position */}
      <TestProgressPanel
        visible={config.showLevelTestProgressPanel}
        currentQuestionNumber={progressData.currentQuestionNumber}
        totalQuestions={progressData.totalQuestions}
        correctAnswers={progressData.correctAnswers}
        timeElapsed={progressData.timeElapsed}
        difficultyLevel={progressData.difficultyLevel}
        phase={progressData.phase}
        phaseGroup={progressData.phaseGroup}
        currentCycleIndex={progressData.currentCycleIndex}
        cycleCorrect={progressData.cycleCorrect}
        cycleTotal={progressData.cycleTotal}
        pendingInCycle={progressData.pendingInCycle}
        poolCandidates={progressData.poolCandidates}
        decisionHint={progressData.decisionHint}
        minLevel={progressData.minLevel}
        maxLevel={progressData.maxLevel}
        levelRangeSize={progressData.levelRangeSize}
      />

      <PageHeader
        showAvatar={true}
        showActions={true}
        title="Teste de Nivelamento"
        subtitle="Descubra seu nível de inglês e receba conteúdo personalizado para seu aprendizado"
      />

      <div className="w-full bg-gray-50 rounded-xl p-6 mt-6">
        <div className="w-[1227px] mx-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-gray-600 text-lg">Carregando teste de nivelamento...</div>
            </div>
          ) : (
            <div ref={questionsContainerRef} className="space-y-6">
              {/* Renderizar histórico (readonly) */}
              {questionsHistory.map((question, index) => {
                const answer = answers.find((a: any) => a.questionId === question.id);
                return (
                  <div key={questionKey(question)} className="opacity-60">
                    {renderQuestion(question, `history-${questionKey(question)}`, index, answer)}
                  </div>
                );
              })}
              
              {/* Renderizar questão atual (editável) */}
              {currentQuestion &&
                renderQuestion(
                  currentQuestion,
                  questionKey(currentQuestion),
                  questionsHistory.length
                )
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// LevelingTest agora exporta diretamente o conteúdo sem o provider wrapper.
// O provider será aplicado no nível de rotas para cobrir todas as páginas de level test.
const LevelingTest: React.FC = () => {
  return <LevelingTestContent />;
};

export default LevelingTest;